import React, { useMemo } from 'react'
import { Typography } from '@material-ui/core'
import { ApartmentVendor, ListVendor } from '../../../../../models'
import { Service } from '../../../../../models/Services/types'
import { useStepLoriStyles } from '../styles'

interface AdvancedLoriServicesProps {
    apartmentVendorList: ApartmentVendor[]
}

type ServiceEntry = {
    service: Service
    vendors: ListVendor[]
}

const sortByName = (a: string, b: string) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })

export const AdvancedLoriServices: React.FC<AdvancedLoriServicesProps> = ({
    apartmentVendorList,
}: AdvancedLoriServicesProps) => {
    const classes = useStepLoriStyles()

    const serviceEntries = useMemo<ServiceEntry[]>(() => {
        const serviceMap = new Map<number, ServiceEntry>()
        const vendorIdsByService = new Map<number, Set<number>>()

        apartmentVendorList.forEach((aptVendor) => {
            const vendor = aptVendor.vendor
            vendor.services?.forEach((service) => {
                if (!serviceMap.has(service.id)) {
                    serviceMap.set(service.id, { service, vendors: [] })
                    vendorIdsByService.set(service.id, new Set<number>())
                }

                const vendorIdSet = vendorIdsByService.get(service.id)
                if (vendorIdSet && !vendorIdSet.has(vendor.id)) {
                    vendorIdSet.add(vendor.id)
                    serviceMap.get(service.id)?.vendors.push(vendor)
                }
            })
        })

        const entries = Array.from(serviceMap.values())
        entries.forEach((entry) => {
            entry.vendors.sort((a, b) => sortByName(a.name, b.name))
        })

        return entries.sort((a, b) => {
            const orderDiff = a.service.order - b.service.order
            if (orderDiff !== 0) return orderDiff
            return sortByName(a.service.name, b.service.name)
        })
    }, [apartmentVendorList])

    return (
        <>
            <Typography component="div" className={classes.servicesHeader}>
                Services & Vendors
            </Typography>
            <div className={classes.servicesBody}>
                {serviceEntries.length === 0 ? (
                    <Typography
                        component="div"
                        className={classes.servicesEmpty}
                    >
                        No services available.
                    </Typography>
                ) : (
                    serviceEntries.map((entry) => (
                        <div
                            key={entry.service.id}
                            className={classes.serviceGroup}
                        >
                            <Typography
                                component="div"
                                className={classes.serviceName}
                            >
                                {entry.service.name}
                            </Typography>
                            <div className={classes.vendorList}>
                                {entry.vendors.map((vendor) => (
                                    <Typography
                                        key={vendor.id}
                                        component="div"
                                        className={classes.vendorItem}
                                    >
                                        {vendor.name}
                                    </Typography>
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </>
    )
}
